import { useState, useRef } from "react";
import { toast } from "sonner";
import { MapPin, Clock, UploadCloud, Loader2, Send } from "lucide-react";
import { Reveal, PageHero, SectionHeading } from "@/components/Common";
import { OPEN_ROLES, COMPANY } from "@/data/site";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fieldCls = "h-12 rounded-none border-slate-300 focus-visible:ring-gold bg-white";

const ApplicationForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Resume required", { description: "Please attach your resume (PDF, DOC or DOCX, max 5 MB)." });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("resume", file);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted.", { description: "Our HR team will review your application and contact you if shortlisted." });
      setForm({ name: "", email: "", phone: "", position: "", experience: "", message: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error("Submission failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="job-application-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="app-name">Full Name *</Label>
          <Input id="app-name" data-testid="application-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={fieldCls} placeholder="Your full name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="app-phone">Phone *</Label>
          <Input id="app-phone" data-testid="application-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={fieldCls} placeholder="+91 ..." />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label htmlFor="app-email">Email *</Label>
          <Input id="app-email" type="email" data-testid="application-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={fieldCls} placeholder="you@email.com" />
        </div>
        <div className="space-y-2">
          <Label>Position Applying For *</Label>
          <Select required value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
            <SelectTrigger className={fieldCls} data-testid="application-position-select">
              <SelectValue placeholder="Select a position" />
            </SelectTrigger>
            <SelectContent>
              {OPEN_ROLES.map((r) => (
                <SelectItem key={r.title} value={r.title} data-testid={`application-position-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{r.title}</SelectItem>
              ))}
              <SelectItem value="Other" data-testid="application-position-other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="app-exp">Experience</Label>
        <Input id="app-exp" data-testid="application-experience-input" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className={fieldCls} placeholder="e.g. 3 years as security guard" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="app-msg">Message</Label>
        <Textarea id="app-msg" data-testid="application-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-none border-slate-300 focus-visible:ring-gold bg-white" placeholder="Anything you'd like us to know..." />
      </div>
      <div className="space-y-2">
        <Label>Resume (PDF / DOC / DOCX, max 5 MB) *</Label>
        <label
          htmlFor="app-resume"
          data-testid="application-resume-upload"
          className="flex items-center justify-center gap-3 border-2 border-dashed border-slate-300 hover:border-gold px-6 py-8 cursor-pointer transition-colors bg-slate-50 text-slate-500 text-sm"
        >
          <UploadCloud size={20} className="text-gold-hover" />
          {file ? <span className="font-medium text-navy">{file.name}</span> : "Click to upload your resume"}
        </label>
        <input
          id="app-resume"
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          data-testid="application-resume-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="application-submit-btn"
        className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-heading font-bold text-xs tracking-widest uppercase px-7 py-4 transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        {submitting ? "Submitting..." : "Submit Application"}
      </button>
    </form>
  );
};

const Careers = () => (
  <>
    <PageHero
      crumb="Home — Careers"
      title="Careers at Daksh"
      subtitle="Join a workforce company with over two decades of operational history. We hire across security, facility, sanitation, warehousing and office-support roles."
    />

    <section className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Open Roles"
          title="Positions We Regularly Hire For"
          desc="Openings vary by site and client requirement. Apply below and our team will reach out when a matching position is available."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {OPEN_ROLES.map((role, i) => (
            <Reveal key={role.title} delay={(i % 3) * 0.07}>
              <div className="border border-slate-200 p-7 h-full transition-all hover:border-gold hover:-translate-y-1 hover:shadow-lg hover:shadow-navy/5" data-testid={`role-${role.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-heading text-lg font-semibold text-navy">{role.title}</h3>
                <div className="mt-4 space-y-2 text-sm text-slate-500">
                  <p className="flex items-center gap-2"><MapPin size={14} className="text-gold-hover" /> {role.location}</p>
                  <p className="flex items-center gap-2"><Clock size={14} className="text-gold-hover" /> {role.type}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 sm:py-20 lg:py-24 bg-[#FAFAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <SectionHeading
            eyebrow="Apply Now"
            title="Submit Your Application"
            desc="Fill in your details and attach your resume. Applications are reviewed by our HR team."
          />
          <Reveal delay={0.1}>
            <div className="bg-navy text-white p-7 space-y-4 hero-grain relative overflow-hidden">
              <p className="text-sm text-slate-300 leading-relaxed">You can also share your resume directly with us:</p>
              <a href={COMPANY.emailHref} className="block text-gold font-medium text-sm break-all hover:text-gold-hover" data-testid="careers-email-link">{COMPANY.email}</a>
              <a href={COMPANY.phoneHref} className="block text-slate-200 text-sm hover:text-gold" data-testid="careers-phone-link">{COMPANY.phone}</a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-3">
          <div className="bg-white border border-slate-200 p-7 sm:p-10">
            <ApplicationForm />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Careers;
